'use client';

import { useMemo, useState } from 'react';

import {
  BookOpen,
  ChevronDown,
  FileText,
  Pencil,
  Search,
  Sparkles,
  SquareTerminal,
  Wrench,
} from 'lucide-react';

import { formatAgentToolName } from '@/components/core/agentThinkingTimeline';
import { cn } from '@/lib/utils';
import { AgentThinkingStep, ChatMessage, ContextMap } from '@/types/chat';

import { contextMapPercent } from '../utils/contextMap';
import {
  formatPageRange,
  libraryChipParts,
  libraryDocumentHref,
  libraryDocumentLabel,
  libraryDocumentPage,
  readLibraryDocuments,
  type LibraryDocumentRef,
} from '../utils/libraryDocuments';
import {
  formatToolOutputForDisplay,
  readExternalResourceLink,
  summarizeToolChipParts,
  type ExternalResourceLink,
} from '../utils/toolOutput';
import {
  readUsageChipDisplay,
  USAGE_TOOL_NAME,
  type UsageTokens,
} from '../utils/usageChip';
import { ConnieContextMap } from './ConnieContextMap';
import { UsageLine } from './UsageLine';

type ToolCallOutput = NonNullable<ChatMessage['toolCallOutputs']>[number];

type ToolIconKind = 'think' | 'write' | 'run' | 'read' | 'search' | 'tool';

type ToolCallRow = {
  id: string;
  toolName: string;
  label: string;
  chips: string[];
  detail: string;
  icon: ToolIconKind;
  sessionId?: string;
  routeName?: string;
  resourceLink?: ExternalResourceLink;
  libraryDocuments?: LibraryDocumentRef[];
  contextMap?: ContextMap;
  usageTokens?: UsageTokens;
};

const chipPillClassName =
  'inline-flex h-[22px] max-w-full min-w-0 shrink items-center truncate rounded-md bg-white/[0.06] px-1.5 font-mono text-[11.5px] text-fg-muted ring-1 ring-inset ring-white/[0.06]';

const RESOURCE_FAVICON = {
  calendar:
    'https://www.gstatic.com/images/branding/product/1x/calendar_2020q4_48dp.png',
  gmail:
    'https://www.gstatic.com/images/branding/product/1x/gmail_2020q4_48dp.png',
  outlook:
    'https://res-1.cdn.office.net/files/fabric/assets/brand-icons/product/png/outlook_48x1.png',
  pipedrive: 'https://www.pipedrive.com/favicon.ico',
} as const;

const ResourceLinkChip = ({ link }: { link: ExternalResourceLink }) => (
  <a
    href={link.href}
    target='_blank'
    rel='noopener noreferrer'
    data-cy='tool-resource-link'
    data-resource={link.service}
    aria-label={`${link.label}, opens in a new tab`}
    title={link.label}
    className='inline-flex h-6 max-w-full shrink-0 items-center gap-1 rounded-md bg-white/[0.06] px-1.5 text-[11.5px] text-white/85 ring-1 ring-inset ring-white/[0.06] transition-colors duration-100 hover:bg-white/[0.1] hover:text-white focus-visible:bg-white/[0.1] focus-visible:outline-none'
  >
    <img
      src={RESOURCE_FAVICON[link.service]}
      alt=''
      width={14}
      height={14}
      draggable={false}
      className='size-3.5 shrink-0'
    />
    <span className='truncate'>{link.label}</span>
  </a>
);

const libraryLinkLabel = (document: LibraryDocumentRef) => {
  const pages = formatPageRange(document.pagesRead);
  return pages
    ? `${libraryDocumentLabel(document)} ${pages}`
    : libraryDocumentLabel(document);
};

const LibraryLinkChip = ({
  document,
  content,
}: {
  document: LibraryDocumentRef;
  content?: string;
}) => (
  <a
    href={libraryDocumentHref(
      document.documentId,
      libraryDocumentPage(document, content)
    )}
    target='_blank'
    rel='noopener noreferrer'
    data-cy='tool-library-link'
    aria-label={`Open the chat for ${libraryDocumentLabel(document)} in Knowledge Base, opens in a new tab`}
    title={libraryLinkLabel(document)}
    className='inline-flex h-6 max-w-full min-w-0 shrink items-center gap-1 rounded-md bg-white/[0.06] px-1.5 text-[11.5px] text-white/85 ring-1 ring-inset ring-white/[0.06] transition-colors duration-100 hover:bg-white/[0.1] hover:text-white focus-visible:bg-white/[0.1] focus-visible:outline-none'
  >
    <BookOpen size={13} strokeWidth={2} className='shrink-0' />
    <span className='truncate'>Open in Library</span>
  </a>
);

const LibraryLinkList = ({
  documents,
  content,
}: {
  documents: LibraryDocumentRef[];
  content?: string;
}) => (
  <ul className='mb-1 flex flex-col gap-0.5' aria-label='Library Documents'>
    {documents.map(document => (
      <li key={document.documentId} className='min-w-0'>
        <a
          href={libraryDocumentHref(
            document.documentId,
            libraryDocumentPage(document, content)
          )}
          target='_blank'
          rel='noopener noreferrer'
          data-cy='tool-library-link'
          className='-mx-1 flex min-h-7 min-w-0 items-center gap-1.5 rounded-md px-1 text-[12px] text-white/85 transition-colors duration-100 hover:bg-white/[0.06] hover:text-white focus-visible:bg-white/[0.06] focus-visible:outline-none'
        >
          <BookOpen size={13} strokeWidth={2} className='shrink-0' />
          <span className='truncate'>{libraryLinkLabel(document)}</span>
        </a>
      </li>
    ))}
  </ul>
);

type ToolCallChipsProps = {
  outputs?: ChatMessage['toolCallOutputs'];
  /** The reply text, whose Page Citations pick the page a Library link opens at. */
  content?: string;
  tools?: string[];
  thinkingSteps?: AgentThinkingStep[];
  /** The turn's Context Map (FSI #1224), opened from the Usage row. */
  contextMap?: ContextMap;
};

const toolIconKind = (toolName: string): ToolIconKind => {
  const name = toolName.toLowerCase();
  if (/usage|think/.test(name)) return 'think';
  if (/write|propos|patch|apply|draft|compos/.test(name)) return 'write';
  if (/run|execut|shell|command/.test(name)) return 'run';
  if (/read|document|file|image/.test(name)) return 'read';
  if (/search|feed|query|retriev|lookup/.test(name)) return 'search';
  return 'tool';
};

const ToolIcon = ({ kind }: { kind: ToolIconKind }) => {
  const iconProps = { size: 13, strokeWidth: 2 } as const;

  switch (kind) {
    case 'think':
      return <Sparkles {...iconProps} />;
    case 'write':
      return <Pencil {...iconProps} />;
    case 'run':
      return <SquareTerminal {...iconProps} />;
    case 'read':
      return <FileText {...iconProps} />;
    case 'search':
      return <Search {...iconProps} />;
    case 'tool':
      return <Wrench {...iconProps} />;
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
};

const formatRunDuration = (steps: AgentThinkingStep[]) => {
  if (!steps.length) return '';
  const first = steps[0];
  const last = steps[steps.length - 1];
  const seconds = Math.max(
    0,
    ((last.endedAt ?? last.startedAt) - first.startedAt) / 1000
  );
  return `${seconds.toFixed(1)}s`;
};

const contextChip = (map: ContextMap) => {
  const percent = contextMapPercent(map);
  return `Context ${map.used > 0 && percent < 1 ? '<1' : percent}%`;
};

const rowsFromOutputs = (
  outputs: ToolCallOutput[],
  contextMap?: ContextMap
): ToolCallRow[] =>
  outputs.map(output => {
    const usage =
      output.toolName === USAGE_TOOL_NAME
        ? readUsageChipDisplay(output.output)
        : null;

    if (usage) {
      return {
        id: output.toolUseId,
        toolName: output.toolName,
        label: formatAgentToolName(output.toolName),
        chips: contextMap
          ? [...usage.tokenChips, contextChip(contextMap)]
          : usage.tokenChips,
        detail: '',
        icon: toolIconKind(output.toolName),
        sessionId: usage.sessionId,
        routeName: usage.routeName,
        contextMap,
        usageTokens: usage.tokens,
      };
    }

    const libraryDocuments = readLibraryDocuments(
      output.toolName,
      output.output
    );

    return {
      id: output.toolUseId,
      toolName: output.toolName,
      label: formatAgentToolName(output.toolName),
      chips:
        libraryChipParts(output.toolName, output.output) ??
        summarizeToolChipParts(output.output, output.toolName),
      detail: formatToolOutputForDisplay(output.output, output.toolName).trim(),
      icon: toolIconKind(output.toolName),
      resourceLink:
        readExternalResourceLink(output.toolName, output.output) ?? undefined,
      libraryDocuments: libraryDocuments.length ? libraryDocuments : undefined,
    };
  });

const rowsFromToolNames = (tools: string[]): ToolCallRow[] =>
  tools.map((toolName, index) => ({
    id: `${toolName}-${index}`,
    toolName,
    label: formatAgentToolName(toolName),
    chips: ['Completed'],
    detail: '',
    icon: toolIconKind(toolName),
  }));

export const ToolCallChips = ({
  outputs,
  content,
  tools,
  thinkingSteps,
  contextMap,
}: ToolCallChipsProps) => {
  const rows = useMemo(() => {
    if (outputs?.length) return rowsFromOutputs(outputs, contextMap);
    if (tools?.length) return rowsFromToolNames(tools);
    return [];
  }, [outputs, tools, contextMap]);

  const [open, setOpen] = useState(true);
  const [openRows, setOpenRows] = useState<Set<string>>(() => new Set());

  const duration = thinkingSteps?.length
    ? formatRunDuration(thinkingSteps)
    : '';
  const summary = `${rows.length} tool call${rows.length === 1 ? '' : 's'}${
    duration ? ` · ${duration}` : ''
  }`;

  const toggleRow = (id: string) => {
    setOpenRows(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (!rows.length) return null;

  return (
    <div className='min-w-0 max-w-full px-2' data-cy='tool-call-chips'>
      <button
        type='button'
        aria-expanded={open}
        onClick={() => setOpen(current => !current)}
        className='-mx-1.5 flex w-fit max-w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-[12.5px] text-fg-muted transition-colors duration-100 hover:bg-white/[0.06] focus-visible:bg-white/[0.06] focus-visible:outline-none'
      >
        <ChevronDown
          size={12}
          className='shrink-0 transition-transform duration-200'
          style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}
        />
        <span className='tabular-nums'>{summary}</span>
      </button>

      <div
        className={cn(
          'grid transition-[grid-template-rows,opacity] duration-300',
          !open && 'pointer-events-none'
        )}
        aria-hidden={!open}
        style={{
          gridTemplateRows: open ? '1fr' : '0fr',
          opacity: open ? 1 : 0,
        }}
      >
        <div className='-mx-1 min-h-0 overflow-hidden px-1.5'>
          <div className='mt-1.5 flex flex-col gap-1'>
            {rows.map(row => {
              const rowOpen = openRows.has(row.id);
              const tokenChips = row.chips.filter(chip => chip.trim());
              const canExpand =
                row.detail.length > 0 ||
                Boolean(row.sessionId) ||
                Boolean(row.usageTokens) ||
                Boolean(row.contextMap);
              const resourceLink = row.resourceLink;
              const libraryDocuments = row.libraryDocuments ?? [];
              const libraryLink =
                libraryDocuments.length === 1 ? libraryDocuments[0] : null;
              const inlineLink = Boolean(resourceLink || libraryLink);

              return (
                <div
                  key={row.id}
                  data-cy='tool-call-row'
                  data-tool-name={row.toolName}
                >
                  <div
                    className={cn(
                      'group/row -mx-[3px] flex min-h-7 w-full min-w-0 items-center gap-x-2 rounded-md px-[3px] py-0.5',
                      inlineLink ? 'flex-nowrap' : 'flex-wrap gap-y-1'
                    )}
                  >
                    <button
                      type='button'
                      aria-expanded={canExpand ? rowOpen : undefined}
                      aria-label={`${row.label}. ${[
                        ...tokenChips,
                        row.routeName,
                      ]
                        .filter(Boolean)
                        .join(', ')}`}
                      onClick={() => {
                        if (canExpand) toggleRow(row.id);
                      }}
                      className={cn(
                        'flex min-h-7 min-w-0 items-center gap-x-2 rounded-md py-0.5 text-left transition-colors duration-100',
                        inlineLink ? 'overflow-hidden' : 'flex-wrap gap-y-1',
                        canExpand
                          ? 'hover:bg-white/[0.06] focus-visible:bg-white/[0.06] focus-visible:outline-none'
                          : 'cursor-default'
                      )}
                    >
                      <span className='relative flex size-4 shrink-0 items-center justify-center text-white/45'>
                        <span
                          className={cn(
                            'transition-opacity duration-100',
                            canExpand && 'group-hover/row:opacity-0',
                            rowOpen && 'opacity-0'
                          )}
                        >
                          <ToolIcon kind={row.icon} />
                        </span>
                        {canExpand ? (
                          <ChevronDown
                            size={12}
                            className={cn(
                              'absolute transition-[opacity,transform] duration-150 group-hover/row:opacity-100',
                              rowOpen ? 'opacity-100' : 'opacity-0'
                            )}
                            style={{
                              transform: rowOpen
                                ? 'rotate(0deg)'
                                : 'rotate(-90deg)',
                            }}
                          />
                        ) : null}
                      </span>
                      <span
                        title={row.label}
                        className={cn(
                          'text-[12.5px] font-medium text-white',
                          inlineLink ? 'min-w-0 shrink truncate' : 'shrink-0'
                        )}
                      >
                        {row.label}
                      </span>
                      {tokenChips.map((chip, chipIndex) => (
                        <span
                          key={`${chip}-${chipIndex}`}
                          title={chip}
                          className={chipPillClassName}
                        >
                          {chip}
                        </span>
                      ))}
                      {row.routeName ? (
                        <span
                          data-cy='usage-route-name'
                          title={row.routeName}
                          className={chipPillClassName}
                        >
                          {row.routeName}
                        </span>
                      ) : null}
                    </button>
                    {resourceLink ? (
                      <ResourceLinkChip link={resourceLink} />
                    ) : null}
                    {libraryLink ? (
                      <LibraryLinkChip
                        document={libraryLink}
                        content={content}
                      />
                    ) : null}
                  </div>

                  {canExpand ? (
                    <div
                      className='grid transition-[grid-template-rows,opacity] duration-300'
                      style={{
                        gridTemplateRows: rowOpen ? '1fr' : '0fr',
                        opacity: rowOpen ? 1 : 0,
                        transitionTimingFunction:
                          'cubic-bezier(0.23, 1, 0.32, 1)',
                      }}
                    >
                      <div className='min-h-0 overflow-hidden'>
                        <div className='mb-1 ml-2 mt-0.5 border-l border-white/10 py-0.5 pl-3.5'>
                          {row.contextMap && rowOpen ? (
                            <div className='mb-2 mt-1'>
                              <ConnieContextMap
                                map={row.contextMap}
                                footer={
                                  <UsageLine
                                    tokens={row.usageTokens}
                                    sessionId={row.sessionId}
                                  />
                                }
                              />
                            </div>
                          ) : null}
                          {!row.contextMap &&
                          (row.usageTokens || row.sessionId) ? (
                            <div className='py-1'>
                              <UsageLine
                                tokens={row.usageTokens}
                                sessionId={row.sessionId}
                              />
                            </div>
                          ) : null}
                          {libraryDocuments.length > 1 ? (
                            <LibraryLinkList
                              documents={libraryDocuments}
                              content={content}
                            />
                          ) : null}
                          {row.detail ? (
                            <pre className='max-h-[min(24rem,50vh)] overflow-auto overscroll-contain font-mono text-[11.5px] leading-[1.6] text-fg-muted whitespace-pre-wrap break-words'>
                              {row.detail}
                            </pre>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
